// src/components/student/StreakCalendar.jsx
// GitHub-style 90-day activity heatmap. Columns stretch to fill the container.
// Columns = weeks (oldest left → newest right). Rows = days of week.
import React, { useMemo } from "react";
import { isoDay } from "../../lib/gamification";

const DAYS = ["S", "M", "T", "W", "T", "F", "S"];

function cellColor(count) {
  if (!count) return "bg-slate-100";
  if (count === 1) return "bg-indigo-200";
  if (count === 2) return "bg-indigo-400";
  return "bg-indigo-600";
}

function formatDate(iso) {
  return new Date(iso + "T00:00:00").toLocaleDateString(undefined, {
    weekday: "short", month: "short", day: "numeric",
  });
}

export default function StreakCalendar({ attempts = [] }) {
  const { grid, months } = useMemo(() => {
    const dayMap = new Map();
    for (const a of attempts) {
      const date = a.takenAt?.toDate ? a.takenAt.toDate() : new Date(a.takenAt);
      if (isNaN(date)) continue;
      const key = isoDay(date);
      dayMap.set(key, (dayMap.get(key) || 0) + 1);
    }

    const days = [];
    for (let i = 89; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const iso = isoDay(d);
      days.push({ iso, count: dayMap.get(iso) || 0 });
    }

    const firstDow = new Date(days[0].iso + "T00:00:00").getDay();
    const padded = [...Array(firstDow).fill(null), ...days];

    const weeks = [];
    for (let i = 0; i < padded.length; i += 7) {
      weeks.push(padded.slice(i, i + 7));
    }

    const monthLabels = [];
    let lastMonth = null;
    weeks.forEach((week) => {
      const firstReal = week.find(Boolean);
      if (!firstReal) { monthLabels.push(""); return; }
      const month = firstReal.iso.slice(0, 7);
      if (month !== lastMonth) {
        const d = new Date(firstReal.iso + "T00:00:00");
        monthLabels.push(d.toLocaleDateString(undefined, { month: "short" }));
        lastMonth = month;
      } else {
        monthLabels.push("");
      }
    });

    return { grid: weeks, months: monthLabels };
  }, [attempts]);

  return (
    <div className="w-full">
      {/* Month labels row — flex-1 columns match the grid columns */}
      <div className="flex gap-1 pl-8 mb-1.5">
        {grid.map((_, wi) => (
          <div key={wi} className="flex-1 min-w-0 text-[10px] text-gray-400 leading-none font-medium truncate">
            {months[wi] || ""}
          </div>
        ))}
      </div>

      <div className="flex gap-1">
        {/* Day-of-week labels */}
        <div className="flex flex-col gap-1 shrink-0 w-7 mr-0.5">
          {DAYS.map((d, i) => (
            <div
              key={i}
              className={`h-4 text-[10px] text-gray-400 leading-none flex items-center font-medium ${i % 2 === 0 ? "" : "invisible"}`}
            >
              {d}
            </div>
          ))}
        </div>

        {/* Grid — each column is flex-1, cells fill full column width */}
        {grid.map((week, wi) => (
          <div key={wi} className="flex-1 flex flex-col gap-1 min-w-0">
            {week.map((day, di) =>
              !day ? (
                <div key={di} className="h-4" />
              ) : (
                <div
                  key={di}
                  className={`h-4 rounded-[3px] cursor-default transition-opacity hover:opacity-75 ${cellColor(day.count)}`}
                  title={`${formatDate(day.iso)} — ${day.count} quiz${day.count !== 1 ? "zes" : ""}`}
                />
              )
            )}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-1.5 mt-3 pl-8">
        <span className="text-[10px] text-gray-400 font-medium">Less</span>
        {[0, 1, 2, 3].map((n) => (
          <div key={n} className={`w-4 h-4 rounded-[3px] ${cellColor(n)}`} />
        ))}
        <span className="text-[10px] text-gray-400 font-medium">More</span>
      </div>
    </div>
  );
}
